/**
 * The Agent module's copy, keyed by the English the component already says.
 *
 * The panel's table maps an invented key to `[en, zh]`. The Agent does not use
 * that shape: ~3,300 literals were written as `t("Save")` before the Agent was
 * Ondit's, and giving each one a key would rewrite every one of them for no
 * reader's benefit. So the English source string *is* the key, `t("Save")`
 * reads as the sentence it renders, and a string with no entry here renders in
 * English — which is what an untranslated Agent screen does today anyway.
 *
 * A `Record<string, string>` rather than a tuple, and it keeps the guarantee
 * the tuple exists for: the key is the English and the value is the Chinese, so
 * the compiler still refuses an entry with only one language. A tuple here
 * would carry the English twice, and the copy that drifted from the call site
 * would be the one nobody reads.
 *
 * Two things this shape cannot do, both deliberate:
 *
 *   - One English word cannot have two Chinese words. "Type" is a noun in a
 *     table header and a verb in a placeholder. When that bites, the call site
 *     is the thing to change — say what the screen means ("Item type") rather
 *     than inventing a key nobody can read.
 *   - Editing the English in a component silently drops its translation. That
 *     is the cost of the English being the key; nothing catches it, so an
 *     edit to a sentence is an edit to its entry here too.
 *
 * Adding to it: append the entry, English exactly as the component writes it —
 * same punctuation, same ellipsis character, same casing. Placeholders are
 * `{name}` and must appear in both halves; the test checks that.
 *
 * Where to put it: one file per surface under `agent-copy/`, because the
 * translation of ~3,300 strings is work several people do at once and a single
 * file is a single thing to collide on. The split is by screen rather than by
 * alphabet so that whoever translates Tables reads a whole surface's vocabulary
 * in one place and keeps it consistent — the part a dictionary cannot check.
 *
 * The same English in two of those files is a duplicate key and the later
 * spread wins silently, so `agent-copy.test.ts` fails on it instead.
 */

import { CATALOG } from "./agent-copy/catalog";
import { CHAT } from "./agent-copy/chat";
import { DATA } from "./agent-copy/data";
import { EXISTING } from "./agent-copy/existing";
import { INTEGRATIONS } from "./agent-copy/integrations";
import { KNOWLEDGE } from "./agent-copy/knowledge";
import { SETTINGS } from "./agent-copy/settings";
import { SHELL } from "./agent-copy/shell";
import { WORKFLOWS } from "./agent-copy/workflows";

export const AGENT_COPY: Record<string, string> = {
	...SHELL,
	...CHAT,
	...WORKFLOWS,
	...DATA,
	...KNOWLEDGE,
	...INTEGRATIONS,
	...SETTINGS,
	...CATALOG,
	...EXISTING,
};
